// Landscape A4 profile pages — slide-driven layout with editorial typography,
// arm-colored chrome, real smart art (orbital model, funnel, circular cycle, mosaic).

import { legacyImages as L } from "@/data/legacyImages";

const IMG = {
  cover: L.heroHeader,
  letter: L.about1,
  trad: L.trad,
  ameer: L.ameer,
  team1: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  team2: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  team3: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  team4: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  team5: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  team6: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&w=500&h=500&fit=crop",
  // Arm hero imagery
  hub: L.storyHub,
  hubA: L.about2,
  hubB: L.about3,
  ent: L.storyWorkshop,
  entA: L.heroEvent,
  emp: L.storyYouth,
  empA: L.services,
  consult: L.ctaBg,
  venture: L.heroHeader,
  // Voices
  voice1: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  voice2: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  voice3: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
  voice4: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&w=400&h=400&fit=crop",
};

const C = { blue: "#3663A8", lightBlue: "#567CB6", orange: "#FF9F1C", magenta: "#E01E84", gray: "#8C8C8C" };

export const profilePages = [
  {
    id: "cover", type: "cover", accent: C.blue,
    eyebrow: "Organisational Profile · 2026",
    title: "International Youth Ambassadors Foundation",
    subtitle: "Building livelihoods. Launching ventures. Sustaining impact.",
    location: "Amman, Jordan",
    image: IMG.cover,
    badges: ["Hub", "Social", "Consult", "Venture"],
  },
  {
    id: "letter", type: "letterSlide", accent: C.blue,
    pageTitle: "01 · A letter from the forum",
    eyebrow: "Foreword",
    pullQuote: "Young people don't lack ambition. They lack infrastructure.",
    body: [
      "IYAF was built around a simple observation: young people don't lack ambition. They lack infrastructure. Spaces to meet. Legal cover to register. A network that picks up the phone. An honest mentor who can say: this is the 97%, redirect; this is the 3%, accelerate.",
      "We are a social enterprise headquartered in Amman, Jordan, operating across four arms (Hub, Social, Consult and Venture) across five countries and thirteen communities.",
    ],
    image: IMG.letter,
    portraits: [IMG.team1, IMG.team2, IMG.team3, IMG.team4, IMG.team5, IMG.team6],
    signature: "The IYAF forum",
  },
  {
    id: "at-a-glance", type: "statsSlide", accent: C.blue,
    pageTitle: "02 · At a glance",
    eyebrow: "At a glance",
    title: "Ten years. Five countries. Real numbers.",
    hero: { v: "4,500+", k: "Youth supported across MENA & East Africa", icon: "users" },
    sub: [
      { v: "2016", k: "Founded", icon: "calendar" },
      { v: "5", k: "Countries", icon: "globe" },
      { v: "13", k: "Communities", icon: "map" },
      { v: "555", k: "Startups assessed", icon: "rocket" },
    ],
    tiles: [
      { v: "1,000", k: "Entrepreneurs trained", icon: "graduate" },
      { v: "3,000", k: "Employment participants", icon: "briefcase" },
      { v: "80%", k: "Employed in 12 months", icon: "trending" },
      { v: "$2M+", k: "Funding secured", icon: "coins" },
      { v: "432", k: "Events hosted", icon: "ticket" },
      { v: "113", k: "Expert mentors", icon: "handshake" },
      { v: "70%", k: "Self-sustainability", icon: "leaf" },
      { v: "100%", k: "Profit reinvested", icon: "refresh" },
    ],
  },
  {
    id: "where", type: "mapSlide", accent: C.blue,
    pageTitle: "03 · Where we work",
    eyebrow: "Footprint",
    title: "Anchored in Amman. Operating across five countries.",
    body: "Programs, partnerships and consulting engagements across the MENA region and East Africa.",
    countries: [
      { name: "Jordan",  status: "HQ",       cx: 60, cy: 44, communities: 5, color: C.blue,    flag: "JO" },
      { name: "Lebanon", status: "Active",   cx: 58, cy: 30, communities: 3, color: C.orange,  flag: "LB" },
      { name: "Egypt",   status: "Active",   cx: 49, cy: 52, communities: 2, color: C.magenta, flag: "EG" },
      { name: "Uganda",  status: "Active",   cx: 64, cy: 74, communities: 2, color: C.gray,    flag: "UG" },
      { name: "Nepal",   status: "Active",   cx: 82, cy: 50, communities: 1, color: C.lightBlue, flag: "NP" },
    ],
    stats: [
      { v: "13", k: "Communities" },
      { v: "5",  k: "Countries" },
      { v: "1",  k: "Forum" },
    ],
  },
  {
    id: "who-we-are", type: "founderSlide", accent: C.blue,
    pageTitle: "04 · Who we are",
    eyebrow: "Who we are",
    title: "It started with 40 youth and a single idea.",
    body: [
      "In 2015, forty young people gathered around a simple belief: when youth are given tools and platforms, they create solutions that matter. In 2016, two of them founded IYAF.",
      "Today we operate across Jordan, Lebanon, Egypt, Uganda and Nepal. 13 communities through one integrated forum.",
    ],
    leadership: [
      { name: "Trad AlMasri", role: "Financial Lead & President", image: IMG.trad },
      { name: "Ameer Mubaslat", role: "Technical Lead & Program Director", image: IMG.ameer },
    ],
    teamStats: [
      { v: "6", k: "Core team" },
      { v: "50", k: "Volunteers" },
      { v: "113", k: "Mentors" },
      { v: "40", k: "Consulting trainers" },
    ],
  },
  {
    id: "the-model", type: "modelSlide", accent: C.blue,
    pageTitle: "05 · The IYAF model",
    eyebrow: "The model",
    title: "Four arms. One integrated forum.",
    body: "The power of IYAF lies in how the arms work together. Hub users become Social participants. Consult clients become sponsors. Social graduates become founders, then employers, then donors.",
    arms: [
      { name: "IYAF Hub", status: "Active", color: C.blue, key: "hub", icon: "building", desc: "400 sqm space, legal umbrella, 113 mentors." },
      { name: "IYAF Social", status: "Active", color: C.orange, key: "social", icon: "rocket", desc: "Entrepreneurship + employment. 97/3 model." },
      { name: "IYAF Consult", status: "Active", color: C.gray, key: "consult", icon: "briefcase", desc: "Professional services. 100% profit reinvested." },
      { name: "IYAF Venture", status: "Launching 2027", color: C.magenta, key: "venture", icon: "trending", desc: "Acceleration for the 3% who clear validation." },
    ],
  },
  {
    id: "hub", type: "armSlide", accent: C.blue,
    pageTitle: "06 · IYAF Hub", armKey: "hub", color: C.blue,
    title: "Where ideas become reality.",
    paragraphs: [
      "400 sqm collaborative space at 44 Queen Noor Street, Amman. Training rooms, meeting rooms, event hall, co-working desks, and the legal umbrella that lets youth-led initiatives launch without forming an entity from day one.",
      "Initiatives hosted include AIESEC Jordan, Sabeen, five TedX teams, Tareeqak Akafak, JNSA, and multiple Model UN clubs.",
    ],
    metrics: [
      { v: "400", k: "Sqm" }, { v: "75", k: "Initiatives" }, { v: "432", k: "Events" },
      { v: "113", k: "Mentors" }, { v: "15", k: "Legal registrations" }, { v: "5K+", k: "Training hrs" },
    ],
    image: IMG.hub,
    collage: [IMG.hubA, IMG.hubB],
    outcomes: [
      "Legal umbrella saves new initiatives 6–12 months of setup",
      "On-demand mentor matching within 48 hours",
      "Event hall hosts 100+ youth gatherings a year",
    ],
  },
  {
    id: "social-ent", type: "ninetyThreeSlide", accent: C.orange,
    pageTitle: "07 · IYAF Social · Entrepreneurship", armKey: "social", color: C.orange,
    title: "The 97/3 model.",
    body: "Research shows ~97% of startups fail. We structured our program around that fact. Everyone gets rigorous validation. The 97% get honest redirection into employment, skills, partnerships. The 3% with genuine potential get intensive risk-mitigation.",
    metrics: [
      { v: "555", k: "Assessed" }, { v: "1,000", k: "Trained" },
      { v: "$2M+", k: "Funding secured" }, { v: "97%", k: "Satisfaction" },
    ],
    ventures: ["Huggsies", "NUR", "Misto Chocolate"],
    image: IMG.ent,
  },
  {
    id: "social-emp", type: "armSlide", accent: C.lightBlue,
    pageTitle: "08 · IYAF Social · Employment", armKey: "social", color: C.lightBlue,
    title: "Skills + access.",
    paragraphs: [
      "We don't 'place' youth in jobs. We teach them to navigate the market. Resume craft, interview prep, technical skills, personal branding, salary negotiation.",
      "Then access: 8,000 employers across 12 industries, 30 career mentors. 80% employed within 12 months.",
    ],
    metrics: [
      { v: "3,000", k: "Participants" }, { v: "80%", k: "Employed (12 mo.)" }, { v: "8,000", k: "Employers" },
      { v: "12", k: "Industries" }, { v: "30", k: "Career mentors" }, { v: "89%", k: "Satisfaction" },
    ],
    image: IMG.emp,
    collage: [IMG.empA, IMG.entA],
    outcomes: [
      "AI-aware resume + interview lab",
      "Direct intros into a 8,000-employer network",
      "Career mentor pairing for 12 months post-program",
    ],
  },
  {
    id: "consult", type: "consultSlide", accent: C.gray,
    pageTitle: "09 · IYAF Consult", armKey: "consult", color: C.gray,
    title: "Professional services. Social purpose.",
    body: "The engine that powers our programs. Services delivered exclusively to organisations with social impact mandates. 100% of profit reinvested into youth programs.",
    services: [
      { name: "Innovation & Strategy", icon: "sparkles" },
      { name: "Program Evaluation", icon: "compass" },
      { name: "Research & Analysis", icon: "search" },
      { name: "Reporting Support", icon: "report" },
    ],
    clients: ["Friedrich Naumann", "UNDP", "Anna Lindh", "Amideast"],
    metrics: [
      { v: "7", k: "Engagements" }, { v: "100%", k: "Profit reinvested" }, { v: "$10–50K", k: "Client savings" },
    ],
    image: IMG.consult,
  },
  {
    id: "venture", type: "armSlide", accent: C.magenta,
    pageTitle: "10 · IYAF Venture", armKey: "venture", color: C.magenta,
    title: "Launching 2027.",
    paragraphs: [
      "Venture closes the loop for the 3% who clear validation. Founding partners and investors welcome.",
      "Planned: 6–12 month incubation · 3–6 month acceleration · investor pitch events · demo days · post-program growth advisory.",
    ],
    metrics: [
      { v: "2027", k: "Launch" }, { v: "20", k: "First cohort" }, { v: "500", k: "VCs in DB" }, { v: "56", k: "Accelerators" },
    ],
    image: IMG.venture,
    outcomes: [
      "Capital partners: family offices, regional VCs, impact funds",
      "Talent partners: ex-founders & operators as EIRs",
      "Pipeline: graduates of Social entrepreneurship cohort",
    ],
  },
  {
    id: "voices", type: "voicesSlide", accent: C.orange,
    pageTitle: "11 · Voices",
    eyebrow: "Voices from the forum",
    title: "What changes when youth get infrastructure.",
    voices: [
      { quote: "IYAF didn't 'place' me. They taught me how to navigate. A year in, I'm leading a team.", name: "Sarah M.", role: "Marketing Lead · Amman", image: IMG.voice1, color: C.lightBlue },
      { quote: "We were redirected, and grateful. The honest 'no' saved us 18 months of burn.", name: "Ahmed K.", role: "Founder · Misto", image: IMG.voice2, color: C.orange },
      { quote: "The Hub's legal umbrella let us run our first event two weeks after applying.", name: "Lina F.", role: "Initiative Lead · Sabeen", image: IMG.voice3, color: C.blue },
      { quote: "I started as a participant. Now I mentor the next cohort. That's the cycle.", name: "Yousef A.", role: "Mentor · Career Track", image: IMG.voice4, color: C.magenta },
    ],
  },
  {
    id: "sustainability", type: "cycleSlide", accent: C.blue,
    pageTitle: "12 · Sustainability",
    eyebrow: "Sustainability",
    title: "We give. We get. We last.",
    body: "70% self-sustainability. 12 months operating reserve. 100% profit reinvested into youth programs.",
    cycle: [
      { name: "Consult engages clients", icon: "handshake" },
      { name: "Revenue generated", icon: "coins" },
      { name: "Profit reinvested", icon: "refresh" },
      { name: "Programs scale", icon: "users" },
      { name: "Graduates mentor", icon: "graduate" },
      { name: "Cycle strengthens", icon: "trending" },
    ],
    pillars: [
      { v: "70%", k: "Self-sustainability", color: C.blue },
      { v: "12 mo.", k: "Operating reserve", color: C.orange },
      { v: "100%", k: "Profit reinvested", color: C.magenta },
    ],
  },
  {
    id: "engage", type: "engageSlide", accent: C.blue,
    pageTitle: "13 · Work with us",
    eyebrow: "How to engage",
    title: "Multiple lanes. One movement.",
    lanes: [
      { name: "Strategic partnership", icon: "handshake", color: C.blue,      desc: "Co-design programs and country expansion." },
      { name: "Sponsorship",            icon: "coins",     color: C.orange,    desc: "Fund specific cohorts, events or initiatives." },
      { name: "Consulting engagement",  icon: "briefcase", color: C.gray,      desc: "Hire Consult. 100% of profit funds youth programs." },
      { name: "Join our programs",      icon: "graduate",  color: C.magenta,   desc: "Apply as a founder or employment participant." },
      { name: "Volunteer or mentor",    icon: "users",     color: C.lightBlue, desc: "Lend skills, time or your network." },
      { name: "Support the mission",    icon: "heart",     color: C.orange,    desc: "Donate. Every dinar is reinvested in youth." },
    ],
  },
  {
    id: "contact", type: "contactSlide", accent: C.blue,
    pageTitle: "14 · Contact",
    title: "Let's connect.",
    body: "We are based in Amman, Jordan, and operate regionally and globally through partnerships.",
    contacts: [
      { k: "Email", v: "info@iyafglobal.com" },
      { k: "Phone", v: "+962 6 569 4177 Ext. 615" },
      { k: "Address", v: "44 Queen Noor Street, Amman 11190, Jordan" },
      { k: "Web", v: "iyafglobal.com" },
      { k: "Instagram", v: "@iyaf.global" },
      { k: "LinkedIn", v: "linkedin.com/company/iyaf" },
    ],
  },
];
